import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import isUndefined from 'lodash/isUndefined';
import { BlueButton } from 'src/components/components/index';
import { InfoSvg, CheckMarkSvg } from 'src/assets/index';
import { TRAVEL_GUARD_LINK, Routes } from 'src/constants/index';
import { formatMoney, renderPriceBasedOn, renderGetawaysDetailsTitle } from 'src/utils/index';
import {
  IGetawayDetails,
  GetawayAmenity,
  GetawayIncludedItem,
  GetawaysLocationEnum,
} from 'src/common-types/index';
import GetawayBackground from 'src/assets/images/getaways/getaways-list-bg.png';
import './style.scss';

const ZERO = 0;
const FOUR = 4;
const MAX_DESCRIPTION_LENGTH = 1250;

interface IProps {
  getaway: IGetawayDetails;
}

interface IState {
  isEllipsis: boolean;
  isImageError: boolean;
}

export class GetawayDetailsInfo extends React.Component<IProps, IState> {
  state: IState = {
    isEllipsis: false,
    isImageError: false,
  };

  groupAmenities = (): GetawayAmenity[][] => {
    const { getaway } = this.props;

    const amenities = getaway.amenities?.reduce((acc, curr) => {
      const currentValue = acc[curr.amenityType] ? [...acc[curr.amenityType], curr] : [curr];

      return { ...acc, [curr.amenityType]: currentValue };
    }, {} as { [field: string]: GetawayAmenity[] });

    return Object.values(amenities);
  };

  onImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
    if (event) {
      this.setState({ isImageError: !this.state.isImageError });
    }
  };

  showMoreToggler = (): void => {
    this.setState({ isEllipsis: !this.state.isEllipsis });
  };

  render(): React.ReactNode {
    const { isEllipsis, isImageError } = this.state;
    const { getaway } = this.props;
    const {
      title,
      promoId,
      videoUrl,
      imageUrl,
      amenities,
      specialType,
      youPayPrice,
      description,
      includedItems,
      costDisclaimer,
      importantInformation,
    } = getaway;
    const { specialTypeName } = specialType;
    const activeGetaway = specialTypeName.toLowerCase();
    const activeGetawaySubcategory = location.pathname.split('/')[FOUR];
    const isTypeNameStaycation = activeGetaway === GetawaysLocationEnum.Staycation;
    const isMore = description.length > MAX_DESCRIPTION_LENGTH;

    return (
      <div className="getaway__container">
        <Link
          className="getaway__details-back"
          to={`${Routes.Getaway}/${activeGetaway}/${activeGetawaySubcategory}`}
        >
          <FormattedMessage id="back.to.list" />
        </Link>
        <div className="getaway__details-wrapper">
          <div className="getaway__details-header">
            <div>
              <h1 className="getaway__details-header_title">{title}</h1>
              <p className="getaway__details-header_promo">
                <FormattedMessage id="promo.id" />: {promoId}
              </p>
            </div>
            <div>
              <p
                className={`getaway__details-header_price ${
                  isTypeNameStaycation ? 'getaway__details-header_price_staycation' : ''
                }`}
              >
                {formatMoney.format(youPayPrice)}{' '}
                <span>{renderPriceBasedOn(activeGetaway, true)}</span>
              </p>
              <Link
                to={`${Routes.GetawayBookingPage}/${promoId}/${activeGetaway}/${
                  !isUndefined(activeGetawaySubcategory) ? activeGetawaySubcategory : ''
                }`}
                className="getaway__details-header_book"
              >
                <BlueButton>
                  <FormattedMessage id="book.getaway" />
                </BlueButton>
              </Link>
            </div>
          </div>
          <div className="getaway__details-media">
            <img
              onError={(event) => this.onImageError(event)}
              className={`getaway__details-media_img ${
                videoUrl ? 'getaway__details-media_item' : ''
              }`}
              src={isImageError || !imageUrl ? GetawayBackground : imageUrl}
              alt={title}
            />
            {videoUrl && (
              <div className="getaway__details-media_item">
                <iframe className="getaway__details-media_item_youtube" src={`${videoUrl}`} />
              </div>
            )}
          </div>
          <div className="getaway__details-content">
            <div className="getaway__details-info">
              <h2 className="getaway__details-info_title">
                {renderGetawaysDetailsTitle(activeGetaway)}
              </h2>
              <div
                dangerouslySetInnerHTML={{
                  __html: description.slice(
                    0,
                    isMore
                      ? !isEllipsis
                        ? MAX_DESCRIPTION_LENGTH
                        : description.length
                      : description.length,
                  ),
                }}
                className="getaway__details-info_text"
              />
              {isMore && (
                <div className="getaway__details-info_more" onClick={this.showMoreToggler}>
                  {isEllipsis ? (
                    <FormattedMessage id="show.less" />
                  ) : (
                    <FormattedMessage id="show.more" />
                  )}
                </div>
              )}
              <h2 className="getaway__details-info_title">
                <FormattedMessage id="important.information" />
              </h2>
              <div
                dangerouslySetInnerHTML={{
                  __html: importantInformation,
                }}
                className="getaway__details-info_text"
              />
              <div className="getaway__details-info_warning">
                <InfoSvg />
                <p className="getaway__details-info_warning_text">{costDisclaimer}</p>
              </div>
            </div>
            <div className="getaway__details-sidebar">
              <div>
                <h2 className="getaway__details-sidebar_title">
                  <FormattedMessage id="whats.included" />
                </h2>
                {!includedItems.length && !amenities.length ? (
                  <p className="getaway__details-sidebar_item_header_text">
                    <FormattedMessage id="no.items.available" />
                  </p>
                ) : (
                  <div>
                    {this.groupAmenities().map((item: GetawayAmenity[], index: number) => (
                      <div
                        key={`${item[ZERO].amenityType}-${index}`}
                        className="getaway__details-sidebar_item"
                      >
                        <div className="getaway__details-sidebar_item_header">
                          <CheckMarkSvg />
                          <p className="getaway__details-sidebar_item_header_text">
                            {item[ZERO].amenityType}
                          </p>
                        </div>
                        {item.map((amenity: GetawayAmenity) => (
                          <p key={amenity.name} className="getaway__details-sidebar_item_text">
                            {amenity.name}
                          </p>
                        ))}
                      </div>
                    ))}
                    {includedItems.map((item: GetawayIncludedItem, index: number) => (
                      <div key={`${item.name}-${index}`} className="getaway__details-sidebar_item">
                        <div className="getaway__details-sidebar_item_header">
                          <CheckMarkSvg />
                          <p className="getaway__details-sidebar_item_header_text">{item.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="getaway__details-sidebar_divider" />
                <p className="getaway__details-info_link">
                  <FormattedMessage id="why.buy.travel" />{' '}
                  <a href={TRAVEL_GUARD_LINK} target="_blank" rel="noreferrer">
                    <FormattedMessage id="click.here" />
                  </a>{' '}
                  <FormattedMessage id="to.learn.why.we.recommend" />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
